import apiSlice from '../apiSlice';
import { GlobalFilterKeys } from '../types';
import { stripGlobalFilterKeys } from '../utils';

import type { Platform } from '@/features/users';

import {
  AdGroupDiagnosticMetric,
  AdGroupDiagnosticParams,
  CampaignDiagnosticMetric,
  EfficiencyInsight,
  EfficiencyInsightParams,
  getStarvationBidderPlatformConfig,
  PerformanceMonitoringData,
  StarvationBidderSearchParams,
} from '@/features/starvationBidder';
import {
  RawTraffickingAdGroupData,
  TraffickingAdGroupData,
} from '@/features/trafficking';
import { PaginationResponse } from '@/shared/utils/types';

const STARVATION_BIDDER_API = 'api/starvation-bidder';

const starvationBidderTags = apiSlice.enhanceEndpoints({
  addTagTypes: ['starvation-bidder'],
});

const starvationBidderApiSlice = starvationBidderTags.injectEndpoints({
  endpoints: (builder) => ({
    getStarvationBidderAdgroups: builder.query<
      TraffickingAdGroupData[],
      GlobalFilterKeys
    >({
      query: () => ({
        url: `${STARVATION_BIDDER_API}/ad-groups/`,
        method: 'GET',
      }),
      transformResponse: (response: RawTraffickingAdGroupData[]) =>
        response.map((entity) => {
          const { adSetId, adSetName, adGroupId, adGroupName, ...other } =
            entity;

          return {
            ...other,
            adGroupId: adGroupId ?? adSetId ?? '',
            adGroupName: adGroupName ?? adSetName ?? '',
          };
        }),
    }),
    getStarvationBidderCampaigns: builder.query<
      PaginationResponse<CampaignDiagnosticMetric>,
      GlobalFilterKeys & StarvationBidderSearchParams
    >({
      query: ({ algorithmStatus, ...params }) => ({
        url: `${STARVATION_BIDDER_API}/detailed-diagnostics/`,
        method: 'GET',
        params: stripGlobalFilterKeys({
          ...params,
          ...(algorithmStatus !== undefined && { algorithmStatus }),
        }),
      }),
    }),

    getStarvationInsightsMetrics: builder.query<
      PaginationResponse<EfficiencyInsight>,
      GlobalFilterKeys &
        EfficiencyInsightParams & { platform: Platform['name'] }
    >({
      query: ({ platform, adgroupId, adsetId, filterDimension, ...params }) => {
        const { insightsAdUnitIdParamKey } =
          getStarvationBidderPlatformConfig(platform);
        const adUnitId =
          insightsAdUnitIdParamKey === 'adsetId' ? adsetId : adgroupId;

        return {
          url: `${STARVATION_BIDDER_API}/insight-explorer/`,
          method: 'GET',
          params: stripGlobalFilterKeys({
            ...params,
            filterDimension,
            [insightsAdUnitIdParamKey]: adUnitId,
          }),
        };
      },
    }),
    getSBPerformanceMonitoring: builder.query<
      PerformanceMonitoringData[],
      GlobalFilterKeys
    >({
      query: () => ({
        url: `${STARVATION_BIDDER_API}/performance-monitoring/`,
        method: 'GET',
      }),
    }),
    getAdGroupDiagnostics: builder.query<
      PaginationResponse<AdGroupDiagnosticMetric>,
      GlobalFilterKeys & AdGroupDiagnosticParams
    >({
      query: ({ diagCampaignOrInsertionId, search, ...params }) => ({
        url: `${STARVATION_BIDDER_API}/detailed-diagnostics/${diagCampaignOrInsertionId}/`,
        method: 'GET',
        params: stripGlobalFilterKeys({
          ...params,
          ...(search !== undefined && { search }),
        }),
      }),
    }),
  }),
});

export const {
  useGetStarvationBidderCampaignsQuery,
  useGetStarvationInsightsMetricsQuery,
  useGetStarvationBidderAdgroupsQuery,
  useGetSBPerformanceMonitoringQuery,
  useGetAdGroupDiagnosticsQuery,
} = starvationBidderApiSlice;
